/**
 * Owns "is the 'Recently compared' list showing?" for a compare setup dialog.
 *
 * Shared by BOTH setup dialogs (Schema Compare R3-4, Data Compare R3-10) so
 * the two cannot drift — the round-3 showcase review asked for one
 * interaction, not two implementations of it.
 *
 * Auto rule: expanded while no connection has been picked, collapsed once one
 * has (before that the list is the fastest way in; after it, it is only
 * pushing SOURCE/TARGET down the dialog). A click on the link button overrides
 * that for as long as the selection state holds; changing the selection state
 * hands control back to the auto rule, so clearing a connection re-expands the
 * list exactly as on a freshly opened dialog.
 *
 * `showLink` is deliberately tied to the selection rather than to `open`: with
 * nothing selected the list is already the first thing on screen, so a header
 * link pointing at it would be the same two words twice. The link appears
 * exactly when the collapse can happen — and then stays put, so re-expanding
 * and re-collapsing use the same control.
 */
import { useRef, useState } from 'react';

export type RecentlyComparedDisclosure = {
  /** Whether the full list renders in the dialog body. */
  open: boolean;
  /** Whether the header link renders at all. */
  showLink: boolean;
  toggle: () => void;
};

export function useRecentlyComparedDisclosure({
  hasSelection,
}: {
  hasSelection: boolean;
}): RecentlyComparedDisclosure {
  const [override, setOverride] = useState<boolean | null>(null);
  const lastHasSelection = useRef(hasSelection);

  if (lastHasSelection.current !== hasSelection) {
    lastHasSelection.current = hasSelection;
    if (override !== null) setOverride(null);
  }

  const open = override ?? !hasSelection;
  return {
    open,
    showLink: hasSelection,
    toggle: () => setOverride(!open),
  };
}
